/**
 * Reconcile an API user payload with the shape the settings form binds to.
 *
 * The form uses flat `*_ids` arrays; the API returns nested objects. Anything
 * that writes a server response into form state must go through here, or the
 * id arrays drift out of step with the nested collections (and the selection
 * cards start rendering a stale selection).
 *
 * Also coerces the nullable strings that inputs can't accept as null.
 */
export default function normalizeUser(data) {
  if (!data) return data
  return {
    ...data,
    gender: data.gender ?? '',
    location: data.location ?? '',
    school: data.school ?? '',
    school_type: data.school_type ?? '',
    skill_ids: data.skills?.map(s => s.id) ?? [],
    community_contribution_ids:
      data.community_contributions?.map(c => c.id) ?? [],
    category_ids: data.categories?.map(c => c.id) ?? [],
  }
}

/** Maps each selection id array to the nested collection it derives from. */
export const SELECTION_COLLECTIONS = {
  skill_ids: 'skills',
  community_contribution_ids: 'community_contributions',
  category_ids: 'categories',
}

/**
 * Minimal patch for one saved selection (skills / causes / contributions).
 *
 * Each selection card saves its field independently, so writing the whole
 * server response back into form state would clobber unsaved edits in the
 * other cards. Instead, take only the saved collection from the response
 * (keeping the id array in step with the nested objects); if the response
 * doesn't include it, fall back to the ids we just saved.
 */
export function selectionPatch(serverData, key, savedIds) {
  const collection = SELECTION_COLLECTIONS[key]
  const items = serverData?.[collection]
  if (Array.isArray(items)) {
    return { [collection]: items, [key]: items.map(item => item.id) }
  }
  return { [key]: savedIds }
}
